import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { IMAGE_ALIGN_CLASS } from "./image-node-view";

export const imageUploadPlaceholderKey = new PluginKey<DecorationSet>(
  "imageUploadPlaceholder",
);

/** Identity for one in-flight upload. An object so no two can collide. */
export type UploadToken = Record<string, never>;

interface AddAction {
  id: UploadToken;
  pos: number;
  /** Local preview URL — `URL.createObjectURL(file)`. */
  previewUrl: string;
  /** Ordering among placeholders queued at the same position. */
  side: number;
}

type PlaceholderAction = { add: AddAction } | { remove: UploadToken };

/**
 * The local preview shown while an image uploads.
 *
 * A **decoration**, deliberately, not a node: the placeholder must never be
 * part of the document. As a node it would be in `getHTML()`, so it would fire
 * `onChange`, and a field committed mid-upload (blur is a commit in
 * `InlineEdit`) would persist a `blob:` URL that is dead the moment the page
 * reloads. A decoration is view-only — nothing observes it, nothing saves it —
 * and ProseMirror maps its position through every edit made while the upload is
 * in flight, so the image lands where it was dropped rather than wherever the
 * caret has since wandered.
 */
function placeholderElement(previewUrl: string): HTMLElement {
  const img = document.createElement("img");
  img.src = previewUrl;
  img.alt = "";
  img.setAttribute("aria-hidden", "true");
  /* Same classes the real node carries, so the swap at the end of the upload
   * changes nothing visible but the fade — same width rules, same alignment,
   * same corners. */
  img.className = `max-w-full ${IMAGE_ALIGN_CLASS.center} rte-image-uploading`;
  return img;
}

export const ImageUploadPlaceholder = Extension.create({
  name: "imageUploadPlaceholder",

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: imageUploadPlaceholderKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            // Carry the placeholders through everything typed around them.
            let next = set.map(tr.mapping, tr.doc);
            const action = tr.getMeta(imageUploadPlaceholderKey) as
              | PlaceholderAction
              | undefined;
            if (action && "add" in action) {
              const deco = Decoration.widget(
                action.add.pos,
                placeholderElement(action.add.previewUrl),
                { id: action.add.id, side: action.add.side },
              );
              next = next.add(tr.doc, [deco]);
            }
            if (action && "remove" in action) {
              next = next.remove(
                next.find(
                  undefined,
                  undefined,
                  (spec) => spec.id === action.remove,
                ),
              );
            }
            return next;
          },
        },
        props: {
          decorations(state) {
            return imageUploadPlaceholderKey.getState(state);
          },
        },
      }),
    ];
  },
});

/**
 * Where a placeholder currently sits, or null once it is gone — the author can
 * delete the paragraph it was dropped into while the upload is still running.
 */
export function findUploadPlaceholder(
  state: EditorState,
  id: UploadToken,
): number | null {
  const set = imageUploadPlaceholderKey.getState(state);
  const found = set?.find(undefined, undefined, (spec) => spec.id === id);
  return found?.length ? found[0].from : null;
}
